import SubscriptionService from "@/services/SubscriptionService";

export const getAccountSubscriptions = async ({ commit }, accountId) => {
  const subscriptionList = await SubscriptionService.getAccountSubscriptions(
    accountId
  );

  commit("setAccountSubscriptions", subscriptionList);

  return subscriptionList;
};

export const fetchAccountPlans = async ({ commit }, { accountId, useCurrencyOptions = false }) => {
  const plans = await SubscriptionService.getAccountPlans(accountId, useCurrencyOptions);

  commit("setAccountPlans", plans);
};

export const updateAccountSubscriptionMultiItem = async ({ commit }, { accountId, payload }) => {
  const response = await SubscriptionService.updateAccountSubscriptionMultiItem(
    accountId,
    payload
  );

  if (!response.paymentIntentClientSecret) {
    const subscriptionList = await SubscriptionService.getAccountSubscriptions(accountId);
    commit("setAccountSubscriptions", subscriptionList);
  }

  return response;
};

const SUBSCRIPTION_POLL_INTERVAL_MS = 2000;
const SUBSCRIPTION_POLL_TIMEOUT_MS = 30000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A change made through the Stripe API only reaches the backend's subscription record when Stripe's webhook
// lands. Re-fetch until `isUpdated(subscription)` holds, then refresh the plan state that depends on it.
// Gives up quietly after the timeout: the plans page still re-fetches when its tab becomes visible again.
// Commits only while the same account is open, since resetAccount empties this module on an account switch.
// Resolves to whether the change was seen.
export const refreshUntilSubscriptionUpdated = async (
  { commit, dispatch, rootGetters },
  { accountId, subscriptionId, isUpdated }
) => {
  const sameAccount = () => rootGetters["Account/accountKey"] === accountId;
  const deadline = Date.now() + SUBSCRIPTION_POLL_TIMEOUT_MS;
  let updated = false;

  while (Date.now() < deadline) {
    await sleep(SUBSCRIPTION_POLL_INTERVAL_MS);

    let subscriptionList;
    try {
      subscriptionList = await SubscriptionService.getAccountSubscriptions(accountId);
    } catch (e) {
      continue;
    }

    if (!sameAccount()) {
      return false;
    }

    commit("setAccountSubscriptions", subscriptionList);

    const subscription = (subscriptionList || []).find(({ id }) => id === subscriptionId);
    if (isUpdated(subscription)) {
      updated = true;
      break;
    }
  }

  if (!sameAccount()) {
    return false;
  }

  await Promise.allSettled([
    dispatch("fetchAccountPlans", { accountId }),
    dispatch("Subscription/fetchAccountPlanFeatures", accountId, { root: true }),
  ]);

  return updated;
};

// A trial upgraded to paid on the same price is done once the subscription stops being "trialing"
// (active, or past_due / incomplete when the charge failed).
export const refreshUntilTrialEnds = ({ dispatch }, { accountId, subscriptionId }) =>
  dispatch("refreshUntilSubscriptionUpdated", {
    accountId,
    subscriptionId,
    isUpdated: (subscription) => subscription?.status !== "trialing",
  });
